import type { CSSProperties, ReactNode } from 'react';
import type { DeepPartial } from '@frameio/utils';
import { DPR, getDisplayCategory } from '@frameio/utils';
import { getThumbSize, SINGLE_THUMB } from '@frameio/utils/thumbnails';
import { MediaThumbnail, theme } from '@frameio/vapor';
import type { FinderAsset } from '../../finder/hooks/useFinderTransferState';
import { getThumbMedia } from '../../finder/utils/getThumbMedia';

type MediaWithFiletype = DeepPartial<FinderAsset> & {
  filetype?: string;
};

type Thumb = {
  id: string;
  type: string;
  media?: MediaWithFiletype;
  mediaType?: string;
};

export type ThumbnailsProps = {
  thumbs: Array<Thumb>;
};

const style = {
  alignContent: 'space-between',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  height: `${SINGLE_THUMB.height}px`,
  justifyContent: 'space-between',
  overflow: 'hidden',
  position: 'relative',
  width: `${SINGLE_THUMB.width}px`,
} as CSSProperties;

export const Thumbnails = ({ thumbs }: ThumbnailsProps) => {
  const Wrapper =
    thumbs.length === 1
      ? ({ children }: { children: ReactNode }) => <>{children}</>
      : ({ children }: { children: ReactNode }) => (
          <div style={style}>{children}</div>
        );

  return (
    <Wrapper>
      {thumbs.map((thumb, index) => {
        const size = getThumbSize(thumbs.length, index);

        return (
          <MediaThumbnail
            key={thumb.id}
            assetTypename={thumb.type || 'UnsupportedAsset'}
            borderRadius={theme.radii[4]}
            data-testid="notification-thumbs"
            displayCategory={getDisplayCategory(
              thumb.media?.filetype,
              thumb.mediaType
            )}
            height={size.height}
            isImageFitPreferred={true}
            isThumbnailFadeInEnabled={false}
            media={
              getThumbMedia(thumb.media, {
                width: size.width * DPR,
                height: size.height * DPR,
              }) ?? {}
            }
            responsive={false}
            width={size.width}
          />
        );
      })}
    </Wrapper>
  );
};
